import { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog.jsx';
import { Button } from '@/components/ui/button.jsx';
import { Input } from '@/components/ui/input.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { FileText, Plus, Trash2 } from 'lucide-react';
import { createTransaction, getBaselinePrefill, getEndorsementWindow } from '@/services/deals.service.js';
import { ApiError } from '@/lib/api.js';

// General policy-transaction authoring (POLICY-TRANSACTIONS-SPEC.md §3/§4).
// Modes:
//   bind         — establish the term baseline (full fleet, snapshot_complete)
//   endorsement  — mid-term change off the latest snapshot (additive)
// A whole-policy cancellation is NOT recorded here (R2k-Q5, deals-door-1 round 3: the create door answers 409
// CANCELLATION_VIA_LPR): the LPR wizard records it, and the signed LPR and the carrier's confirmation have their own doors.
// An endorsement states its KIND when it is the carrier's reinstatement (R2k-Q6: endorsement_kind 'reinstatement'), which is
// what lets a Cancelled deal be reopened.
// AR/AP legs surface only on a RETURN (negative premium): the agency-bill
// statement-credit case — AR = insured account credit, AP = carrier statement
// credit. Charges go through the existing payment-request flow, so no legs here.

const blankAircraft = () => ({
  tail_number: '', aircraft_id: null, make_model: '', year: '',
  hull_value: '', deductible_in_motion: '', deductible_not_in_motion: '',
  coverage_type: '', aircraft_use: '', annual_premium: '',
  _remove: false, _new: true,
});

function num(v) { const n = Number(v); return Number.isFinite(n) ? n : 0; }
function money(n) { return '$' + num(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }

export default function TransactionCreateSheet({
  open, onOpenChange, dealId, hasBaseline, latestSnapshot, priorTransactionId, onCreated,
}) {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [type, setType] = useState('bind');
  const [endorsementKind, setEndorsementKind] = useState(''); // '' (an ordinary endorsement) or 'reinstatement', never inferred
  const [effectiveDate, setEffectiveDate] = useState('');
  const [endDate, setEndDate] = useState(''); // bind: the policy term end; endorsement: its own end
  // Endorsement end (deals-door-1 T15, PLAN-FINAL 8.2): prefilled with the server's end_default and sent only when the
  // broker touched it, so an untouched end stays NULL ("to the policy's expiration") and follows a term move.
  const [endTouched, setEndTouched] = useState(false);
  // The server's endorsement window (GET endorsement-window): defaults, min/max and the hint. Null until read or when the
  // read failed; then no bound is shown and the create door decides.
  const [endorsementWin, setEndorsementWin] = useState(null);
  const [description, setDescription] = useState('');
  const [premium, setPremium] = useState('');
  const [tax, setTax] = useState('');
  const [commission, setCommission] = useState('');
  const [commissionMode, setCommissionMode] = useState('dollar'); // 'dollar' | 'pct'
  const [aircraft, setAircraft] = useState([]);
  const [baseSnapshot, setBaseSnapshot] = useState(null);

  // AR/AP legs (return only)
  const [arStatus, setArStatus] = useState('waived');
  const [arAmount, setArAmount] = useState('');
  const [apStatus, setApStatus] = useState('succeeded');
  const [apAmount, setApAmount] = useState('');
  // Deferred collection (David 2026-08-12): a broker's deliberate hold —
  // visible to David with its reason, silent to the client. Never a default.
  const [deferPayment, setDeferPayment] = useState(false);
  const [deferReason, setDeferReason] = useState('');
  // Hold the return as an account credit instead of a cash refund
  // (REWRITE-AND-CREDITS-SPEC §B3.1). Applies the AR amount to a future payment
  // request; the AR leg records method credit_hold.
  const [holdAsCredit, setHoldAsCredit] = useState(false);
  const [creditMemo, setCreditMemo] = useState('');

  useEffect(() => {
    if (!open || !dealId) return undefined;
    let cancelled = false;
    // reset
    setSubmitting(false);
    setEndorsementKind('');
    setDescription(''); setPremium(''); setTax(''); setCommission(''); setCommissionMode('dollar');
    setEndDate(''); setEndTouched(false); setEndorsementWin(null);
    setArStatus('waived'); setArAmount(''); setApStatus('succeeded'); setApAmount('');

    if (!hasBaseline) {
      // Baseline mode — prefill the bind from CRM (editable).
      setType('bind');
      setLoading(true);
      getBaselinePrefill(dealId)
        .then((r) => {
          const p = r?.prefill || {};
          const snap = p.snapshot || {};
          setBaseSnapshot(snap);
          setEffectiveDate(p.effective_date || '');
          setEndDate(p.end_date || ''); // bind requires end_date (term end)
          setPremium(p.premium_delta != null ? String(p.premium_delta) : '');
          setTax(p.tax_delta != null ? String(p.tax_delta) : '');
          setAircraft((snap.aircraft || []).map((ac) => ({
            ...blankAircraft(), ...ac,
            hull_value: ac.hull_value ?? '', annual_premium: ac.annual_premium ?? '',
            _new: false,
          })));
        })
        .catch((e) => toast.error(`Couldn't load baseline: ${e.message || 'unknown'}`))
        .finally(() => setLoading(false));
    } else {
      // Endorsement mode off the latest snapshot.
      setType('endorsement');
      setBaseSnapshot(latestSnapshot || null);
      setEffectiveDate('');
      setAircraft(((latestSnapshot?.aircraft) || []).map((ac) => ({
        ...blankAircraft(), ...ac,
        hull_value: ac.hull_value ?? '', annual_premium: ac.annual_premium ?? '',
        _new: false,
      })));
      // The window is the server's (utils/endorsement-dates endorsementWindow over policy_period): render it as given.
      setLoading(true);
      getEndorsementWindow(dealId)
        .then((r) => {
          if (cancelled) return;
          const w = r?.window || {};
          setEndorsementWin({ ...w, hint: r?.hint || '' });
          setEffectiveDate(w.effective_default || '');
          setEndDate(w.end_default || '');
        })
        .catch((e) => { if (!cancelled) toast.error(`Couldn't load the endorsement window: ${e.message || 'unknown'}. The dates are checked when you record.`); })
        .finally(() => { if (!cancelled) setLoading(false); });
    }
    return () => { cancelled = true; };
  }, [open, dealId, hasBaseline, latestSnapshot]);

  const premiumNum = num(premium);
  const taxNum = num(tax);
  // Commission can be entered as a dollar amount or a percent of the premium Δ.
  // In % mode the sign follows premium_delta automatically (premium 14446 @ 15%
  // → 2166.90; return −704.80 @ 15% → −105.72).
  // A BLANK COMMISSION IS UNKNOWN, NOT ZERO (2026-10-01). num('') is 0, so a
  // box left empty used to send commission_delta 0 and the row said the agency
  // earned nothing and the carrier was owed the whole premium. Blank sends no
  // commission at all (the door writes null and derives a null net); a typed 0
  // is still 0. Guard: tests/create-sheet-commission-blank.test.js.
  const commissionStated = String(commission).trim() !== '';
  const commissionNum = !commissionStated ? null
    : commissionMode === 'pct'
      ? Math.round(premiumNum * num(commission) / 100 * 100) / 100
      : num(commission);
  const netToCarrier = commissionNum === null ? null : Math.round((premiumNum + taxNum - commissionNum) * 100) / 100;
  const isReturn = premiumNum < 0;

  // Default leg magnitudes when it becomes a return.
  useEffect(() => {
    if (!isReturn) return;
    if (arAmount === '') setArAmount(String(Math.abs(Math.round((premiumNum + taxNum) * 100) / 100)));
    if (apAmount === '' && netToCarrier !== null) setApAmount(String(Math.abs(netToCarrier)));
  }, [isReturn]); // eslint-disable-line react-hooks/exhaustive-deps

  const isBind = type === 'bind';
  const isEndorsement = type === 'endorsement';
  // A server bound, or no attribute at all (never an invented one).
  const winBound = (k) => (isEndorsement && endorsementWin?.[k]) || undefined;
  const keptAircraft = useMemo(() => aircraft.filter((a) => !a._remove), [aircraft]);
  const snapshotTAP = useMemo(
    () => Math.round(keptAircraft.reduce((s, a) => s + num(a.annual_premium), 0) * 100) / 100,
    [keptAircraft],
  );

  function setAc(i, patch) {
    setAircraft((rows) => rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  }

  function buildSnapshot() {
    const canonical = keptAircraft.map((a) => ({
      tail_number: a.tail_number || null,
      aircraft_id: a.aircraft_id || null,
      hull_value: a.hull_value === '' ? null : num(a.hull_value),
      deductible_in_motion: a.deductible_in_motion || null,
      deductible_not_in_motion: a.deductible_not_in_motion || null,
      coverage_type: a.coverage_type || null,
      aircraft_use: a.aircraft_use || null,
      annual_premium: a.annual_premium === '' ? null : num(a.annual_premium),
    }));
    // The latest snapshot is copied, but never the roster journal's own provenance (a roster_apply source) or a smooth answer a
    // restatement copied (liability_smooth with its mark liability_smooth_from): this sheet asks neither, and the server drops both.
    const base = { ...(baseSnapshot || {}) };
    if (typeof base.source === 'string' && base.source.startsWith('roster_apply:')) delete base.source;
    const coverage = { ...(base.coverage || {}) };
    if (coverage.liability_smooth_from != null) { delete coverage.liability_smooth; delete coverage.liability_smooth_from; }
    return {
      ...base,
      // Bind baseline is authoritative (full sync); endorsement/cancellation is
      // additive-only so it can't delete CRM rows the broker didn't enumerate.
      snapshot_complete: isBind,
      coverage,
      aircraft: canonical,
    };
  }

  async function handleSubmit() {
    if (!effectiveDate) { toast.error('Effective date is required'); return; }
    if (isBind && !endDate) { toast.error('Policy term end date is required for a bind'); return; }
    // Bare YYYY-MM-DD on both sides, so string order is calendar order. A term
    // that ends the day it starts is not a term (Wall / N9364P, 2026-09-30).
    if (isBind && endDate <= effectiveDate) { toast.error(`Policy term end must be after the effective date (${effectiveDate} to ${endDate})`); return; }
    if (isBind && keptAircraft.length === 0) { toast.error('A baseline bind needs at least one aircraft'); return; }
    if (isBind && keptAircraft.some((a) => !a.tail_number.trim())) { toast.error('Every aircraft needs a tail number'); return; }

    const payload = {
      type,
      effective_date: effectiveDate,
      // end_date is REQUIRED on bind, optional on an endorsement (sent only when
      // the broker touched it; blank or untouched runs to the policy's
      // expiration), and refused on a cancellation.
      ...(isBind ? { end_date: endDate } : {}),
      ...(isEndorsement && endTouched && endDate ? { end_date: endDate } : {}),
      ...(isEndorsement && endorsementKind ? { endorsement_kind: endorsementKind } : {}),
      premium_delta: premiumNum,
      tax_delta: taxNum,
      ...(commissionNum !== null ? { commission_delta: commissionNum } : {}),
      description: description.trim() || undefined,
      policy_snapshot: buildSnapshot(),
      ...(type !== 'bind' && priorTransactionId ? { prior_transaction_id: priorTransactionId } : {}),
    };
    if (!isReturn && deferPayment) {
      if (!deferReason.trim()) { toast.error('A deferral requires its reason — one with none is indistinguishable from a mistake'); return; }
      payload.ar_leg = { status: 'deferred', deferred_reason: deferReason.trim() };
    }
    if (isReturn) {
      payload.ar_leg = { status: arStatus, amount: num(arAmount), ref: 'insured account credit' };
      payload.ap_leg = { status: apStatus, amount: num(apAmount), ref: 'carrier statement credit' };
      if (holdAsCredit) {
        if (!(num(arAmount) > 0)) { toast.error('Hold as credit needs an insured credit (AR) amount > 0'); return; }
        payload.hold_as_credit = true;
        if (creditMemo.trim()) payload.credit_memo = creditMemo.trim();
      }
    }

    setSubmitting(true);
    try {
      const res = await createTransaction(dealId, payload);
      if (!res?.success) { toast.error(res?.error || 'Create failed'); return; }
      // The transaction committed, but the account credit did NOT (the txn is
      // already on the ledger). Surface it loudly so the broker adds the credit
      // by hand rather than losing the held money silently.
      if (res.credit_error) {
        toast.error(`Transaction saved, but the account credit was NOT created: ${res.credit_error}. Add it manually from the account money zone.`);
      } else {
        toast.success(`${isBind ? 'Baseline' : endorsementKind === 'reinstatement' ? 'Reinstatement endorsement' : type.charAt(0).toUpperCase() + type.slice(1)} recorded${holdAsCredit ? ' · credit held' : ''}`);
      }
      // The door's FYIs (backdated, term unknown, term unverified): one toast.info each, on this surface.
      for (const w of (Array.isArray(res.warnings) ? res.warnings : [])) toast.info(w);
      onCreated?.();
    } catch (err) {
      const msg = err instanceof ApiError ? (err.body?.error || err.code) : (err.message || 'request failed');
      toast.error(`Couldn't record transaction: ${msg}`);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText size={18} className="text-accent" />
            {isBind ? 'Establish policy baseline' : 'Record policy transaction'}
          </DialogTitle>
          <DialogDescription>
            {isBind
              ? 'Baseline for THIS policy term — the bind that starts this deal’s ledger. Prefilled from CRM; edit to match term-start (e.g. add back a sold/totaled aircraft, set the original premium). All mid-term changes for this term chain off this. The renewal gets its own bind on the renewal deal.'
              : 'Record a mid-term change on this term. A whole-policy cancellation goes through the Lost Policy Release (LPR) wizard, not here.'}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center text-sm text-text-muted">{isBind ? 'Loading baseline…' : 'Loading the endorsement window…'}</div>
        ) : (
          <div className="my-2 space-y-4">
            {/* Type + date */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-xs font-medium text-text-muted">Type</label>
                <select
                  className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-text focus:outline-none focus:ring-1 focus:ring-accent disabled:opacity-60"
                  value={type}
                  disabled={!hasBaseline}
                  onChange={(e) => setType(e.target.value)}
                >
                  {!hasBaseline ? (
                    <option value="bind">Bind (baseline)</option>
                  ) : (
                    <option value="endorsement">Endorsement</option>
                  )}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-text-muted">{isBind ? 'Effective (term start)' : 'Effective date'}</label>
                <Input type="date" aria-label="Effective date" value={effectiveDate} min={winBound('effective_min')} max={winBound('effective_max')} onChange={(e) => setEffectiveDate(e.target.value)} />
              </div>
              {isBind ? (
                <div>
                  <label className="mb-1 block text-xs font-medium text-text-muted">Policy term end</label>
                  <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                </div>
              ) : null}
              {isEndorsement ? (
                <div>
                  <label className="mb-1 block text-xs font-medium text-text-muted">Endorsement end</label>
                  <Input type="date" aria-label="Endorsement end" value={endDate} min={winBound('end_min')} max={winBound('end_max')} onChange={(e) => { setEndDate(e.target.value); setEndTouched(true); }} />
                </div>
              ) : null}
            </div>
            {isEndorsement ? (
              <div>
                <label className="mb-1 block text-xs font-medium text-text-muted">Kind</label>
                <select
                  aria-label="Endorsement kind"
                  className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-text focus:outline-none focus:ring-1 focus:ring-accent"
                  value={endorsementKind}
                  onChange={(e) => setEndorsementKind(e.target.value)}
                >
                  <option value="">Endorsement (a change to the policy)</option>
                  <option value="reinstatement">Reinstatement (the carrier reinstated a cancelled policy)</option>
                </select>
              </div>
            ) : null}
            {isEndorsement && endorsementWin?.hint ? (
              <div className="-mt-2 text-[11px] text-text-dim">{endorsementWin.hint}</div>
            ) : null}

            {/* Money */}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="mb-1 block text-xs font-medium text-text-muted">Premium Δ</label>
                <Input type="number" step="0.01" value={premium} onChange={(e) => setPremium(e.target.value)} placeholder={isBind ? '14446.00' : '-704.80'} />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-text-muted">Tax Δ</label>
                <Input type="number" step="0.01" value={tax} onChange={(e) => setTax(e.target.value)} placeholder="0" />
              </div>
              <div>
                <div className="mb-1 flex items-center justify-between gap-2">
                  <label className="text-xs font-medium text-text-muted">Commission Δ</label>
                  <div className="flex overflow-hidden rounded border border-border">
                    {[['dollar', '$'], ['pct', '%']].map(([m, sym]) => (
                      <button
                        key={m}
                        type="button"
                        onClick={() => setCommissionMode(m)}
                        className={`px-1.5 text-[10px] leading-4 ${commissionMode === m ? 'bg-accent text-white' : 'text-text-dim hover:text-text'}`}
                      >
                        {sym}
                      </button>
                    ))}
                  </div>
                </div>
                <Input type="number" step="0.01" value={commission} onChange={(e) => setCommission(e.target.value)} placeholder={commissionMode === 'pct' ? '15' : '-105.72'} />
                {commissionMode === 'pct' && commissionNum !== null ? (
                  <div className="mt-0.5 font-mono text-[10px] text-text-dim">= {money(commissionNum)}</div>
                ) : null}
              </div>
            </div>
            <div className="text-[11px] text-text-dim font-mono">
              Net to carrier (premium + tax − commission) = <span className="text-text">{netToCarrier === null ? 'unknown until the commission is stated' : money(netToCarrier)}</span>
            </div>

            {/* Aircraft */}
            <div>
              <div className="mb-1 flex items-center justify-between">
                <label className="text-xs font-medium text-text-muted">Aircraft ({keptAircraft.length})</label>
                <Button size="sm" variant="ghost" onClick={() => setAircraft((r) => [...r, blankAircraft()])}>
                  <Plus size={13} strokeWidth={1.5} /> Add
                </Button>
              </div>
              <ul className="space-y-2">
                {aircraft.map((a, i) => (
                  <li key={i} className={`rounded-md border border-border p-2 ${a._remove ? 'opacity-50' : ''}`}>
                    <div className="flex items-center gap-2">
                      <Input className="w-28" value={a.tail_number} onChange={(e) => setAc(i, { tail_number: e.target.value.toUpperCase() })} placeholder="N12345" />
                      <Input className="flex-1" value={a.make_model || ''} onChange={(e) => setAc(i, { make_model: e.target.value })} placeholder="Make / model" />
                      <Input className="w-28" type="number" step="0.01" value={a.annual_premium} onChange={(e) => setAc(i, { annual_premium: e.target.value })} placeholder="premium" />
                      {!isBind && !a._new ? (
                        <Button size="sm" variant={a._remove ? 'outline' : 'ghost'} onClick={() => setAc(i, { _remove: !a._remove })} title={a._remove ? 'Keep' : 'Remove'}>
                          <Trash2 size={14} strokeWidth={1.5} className={a._remove ? 'text-text-dim' : 'text-accent'} />
                        </Button>
                      ) : (
                        <Button size="sm" variant="ghost" onClick={() => setAircraft((r) => r.filter((_, idx) => idx !== i))} title="Remove row">
                          <Trash2 size={14} strokeWidth={1.5} className="text-accent" />
                        </Button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
              <div className="mt-1 text-[11px] font-mono text-text-dim">TAP (Σ annual premium) = <span className="text-text">{money(snapshotTAP)}</span></div>
            </div>

            {/* AR/AP legs — return only */}
            {isReturn ? (
              <div className="rounded-md border border-success/40 bg-success-soft/30 p-3 space-y-2">
                <div className="text-xs font-medium text-text">Return settlement (agency-bill statement credit)</div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="mb-1 block text-[11px] text-text-muted">Insured credit (AR)</label>
                    <Input type="number" step="0.01" value={arAmount} onChange={(e) => setArAmount(e.target.value)} placeholder="704.80" />
                  </div>
                  <div>
                    <label className="mb-1 block text-[11px] text-text-muted">Carrier statement credit (AP)</label>
                    <Input type="number" step="0.01" value={apAmount} onChange={(e) => setApAmount(e.target.value)} placeholder="599.08" />
                  </div>
                </div>
                <div className="text-[10px] text-text-dim">Settled on record — no cash moves (insured account credit + carrier statement credit).</div>
                <label className="flex items-start gap-2 border-t border-success/30 pt-2 text-[11px] text-text">
                  <input type="checkbox" className="mt-0.5" checked={holdAsCredit} onChange={(e) => setHoldAsCredit(e.target.checked)} />
                  <span>
                    Hold the insured (AR) amount as an <strong>account credit</strong> instead of a cash refund.
                    Apply it to a future payment request on this or another deal.
                  </span>
                </label>
                {holdAsCredit && (
                  <Input value={creditMemo} onChange={(e) => setCreditMemo(e.target.value)} placeholder="Credit memo (optional)" />
                )}
              </div>
            ) : null}

            {!isReturn ? (
              <div className="rounded-md border border-warning/40 bg-warning/10 p-3 space-y-2">
                <label className="flex items-start gap-2 text-[11px] text-text">
                  <input type="checkbox" className="mt-0.5" checked={deferPayment} onChange={(e) => setDeferPayment(e.target.checked)} />
                  <span>
                    <strong>Defer collection.</strong> Deliberately unpaid: stays on your radar with its
                    reason, never asks the client for money, never auto-collected, never voided by a re-bind.
                  </span>
                </label>
                {deferPayment && (
                  <Input value={deferReason} onChange={(e) => setDeferReason(e.target.value)}
                    placeholder="Why (required), e.g. bound on goodwill, collect at renewal" />
                )}
              </div>
            ) : null}

            <div>
              <label className="mb-1 block text-xs font-medium text-text-muted">Description</label>
              <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder={isReturn ? 'N47253 sold — pro-rata return' : 'Mid-term change'} />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting || loading}>
            {submitting ? 'Recording…' : isBind ? 'Establish baseline' : 'Record transaction'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
